import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import features from '../features'
import settings from '../settings'
import Headline from '../typography/headline'
import {
  ensureSubsystem,
  hideByVisibility,
  hideFormMeetingTypeIfMatchingSlot,
  isAdminEnabled,
  isDisabled,
  matchingSlotType,
} from '../../../utils/meetingType'
import { getState } from '../../../utils/state'
import { mobileCheck, toSimpleService } from '../../containers/bada/utils'
import { MeetingTypeList } from '../meeting-types/MeetingTypes'
import MeetingTypeItem from './MeetingTypeItem'
import { SIMPLY_BOOK_ME } from '../../containers/bada/reducer/Wizard'
import { useCms } from '../cms/CmsContext'
import { isDesigner } from '../../../utils/initValues'

const HeadlineDesktop = styled(Headline)`
  display: none;
  margin-bottom: 20px;
  text-transform: ${theme('typography.subHeadline.textTransform')};
  line-height: ${theme('typography.subHeadline.lineHeightDesktop')};
  ${(p) => p.theme.media.min.md`
    display: block;
  `};
`

const HeadlineMobile = styled(Headline)`
  margin-bottom: 20px;
  text-transform: ${theme('typography.subHeadline.textTransform')};
  ${(p) => p.theme.media.min.md`
    display: none;
  `}
`
const showAllMeetings =
  isDesigner && features.showDisabledHiddenMeetingTypesForDesigners

export const filterMeetingTypes = (
  meetingtypes,
  selectedStore,
  prepformOnly,
  categoryId,
  client,
  flow,
  selectedServiceProvider
) =>
  meetingtypes
    .filter(
      (meetingType) =>
        (!isDisabled(meetingType.id, selectedStore.services) &&
          !meetingType.designerOnly) ||
        (showAllMeetings &&
          (meetingType.isFollowUpMeeting ||
            meetingType.designerOnly ||
            isAdminEnabled(meetingType.id, selectedStore.services)))
    )
    .filter((meetingType) =>
      ensureSubsystem(
        meetingType,
        selectedStore.simplyBookMeSubsystem,
        settings.serviceMappings
      )
    )
    .filter((meetingType) =>
      matchingSlotType(
        meetingType.serviceType,
        selectedStore.bookingSystem,
        selectedStore.hasSimplyBookMe
      )
    )
    .filter(
      (meetingType, index, result) =>
        !hideFormMeetingTypeIfMatchingSlot(
          meetingType.serviceType,
          meetingType.id,
          result
        )
    )
    .filter(
      (meetingType) =>
        !hideByVisibility(
          meetingType.id,
          meetingType.visibility,
          selectedStore.bookingSystem,
          selectedStore.hasSimplyBookMe,
          selectedStore.acceptHomeVisits,
          !!prepformOnly,
          features.allowDisableHomeVisits
        )
    )
    .filter((meetingType) => {
      if (categoryId && client === 'kratos') {
        return `${meetingType.categoryId}` === `${categoryId}`
      }
      return true
    })
    .filter((meetingType) => {
      if (showAllMeetings) {
        return true
      }
      if (flow === 'followup') {
        if (meetingType.isFollowUpMeeting) {
          if (meetingType.code === 'followupvideo') {
            return selectedServiceProvider?.virtual
              ? meetingType.duration === 60
              : meetingType.duration === 90
          }
          return !selectedServiceProvider?.virtual
        }
        return meetingType.isFollowUpMeeting
      }
      return !meetingType.isFollowUpMeeting
    })

const MeetingTypeSelection = ({
  onChange,
  selectedService,
  selectedStore,
  appointmentType,
  flow,
  selectedServiceProvider,
}) => {
  const {
    services: contentServices,
    pageContent: {
      serviceSelectionHeadingDesktop,
      serviceSelectionHeadingMobile,
    },
  } = useCms()

  const [meetingTypes, setMeetingTypes] = useState([])
  const [preSelectedId, setPreSelectedId] = useState(null)
  const [disabled, setDisabled] = useState(false)
  const [skipStep, setSkipStep] = useState(false)

  const params = new URLSearchParams(window.location.search)
  const usePrepFormOnly = params.get('prepform')

  function meetingTypeChanged(service) {
    if (service.serviceType === SIMPLY_BOOK_ME) {
      setDisabled(true)
    }
    onChange({
      selectedService: toSimpleService(service),
      selectedSlot: null,
      overrideMeetingType: null,
      preSelectedMeetingTypeId: null,
      bookingCompleted: false,
    })

    const timeout = setTimeout(() => {
      setDisabled(false)
      clearTimeout(timeout)
    }, 2500)
  }

  useEffect(() => {
    const fetchedMeetingTypes = filterMeetingTypes(
      contentServices,
      selectedStore,
      !!usePrepFormOnly,
      null,
      null,
      flow,
      selectedServiceProvider
    )

    setSkipStep(false)
    if (fetchedMeetingTypes.length === 1) {
      const meetingType = fetchedMeetingTypes[0]
      meetingTypeChanged(meetingType)
      setSkipStep(true)
    }
    setMeetingTypes(fetchedMeetingTypes)

    const state = getState(settings.brand)
    const preSelectedMeetingTypeId = state?.preSelectedMeetingTypeId

    setPreSelectedId(preSelectedMeetingTypeId)
  }, [selectedStore, selectedServiceProvider])

  useEffect(() => {
    const preselectedMeetingType =
      meetingTypes &&
      meetingTypes.find((c) => c.id === parseInt(preSelectedId, 10))

    if (preselectedMeetingType) {
      onChange({
        selectedService: toSimpleService(preselectedMeetingType),
        selectedSlot: null,
        overrideMeetingType: null,
        preSelectedMeetingTypeId: preSelectedId,
      })
    }
  }, [meetingTypes, preSelectedId])

  if (!selectedStore) {
    return null
  }

  if (!contentServices || contentServices.length < 1) {
    return <h1>No services setup in CMS</h1>
  }

  if (selectedStore.hideInBada && selectedStore.hideInBada === true) {
    return null
  }

  return (
    <>
      {(!skipStep || features.showSingleMeetingType === true) && (
        <>
          {serviceSelectionHeadingDesktop && (
            <HeadlineDesktop>{serviceSelectionHeadingDesktop}</HeadlineDesktop>
          )}
          {serviceSelectionHeadingMobile && (
            <HeadlineMobile>{serviceSelectionHeadingMobile}</HeadlineMobile>
          )}

          <MeetingTypeList>
            {meetingTypes &&
              meetingTypes.map((service) => (
                <MeetingTypeItem
                  key={`${service.id}-${service.serviceType}`}
                  onChange={() => meetingTypeChanged(service)}
                  props={{
                    id: service.code,
                    name: service.heading,
                    text: (() => {
                      if (mobileCheck()) {
                        return isDesigner &&
                          service.descriptionMobileForDesigner
                          ? service.descriptionMobileForDesigner
                          : service.descriptionMobile
                      }
                      return isDesigner && service.descriptionDesktopForDesigner
                        ? service.descriptionDesktopForDesigner
                        : service.descriptionDesktop
                    })(),
                    icon: service.icon,
                    checked:
                      !!selectedService &&
                      selectedService.id === service.code &&
                      selectedService.serviceId === service.id,
                    serviceId: service.id,
                    serviceCode: service.serviceCode,
                    serviceType: service.serviceType,
                    serviceDuration: service.duration,
                    appointmentType,
                    disabled,
                  }}
                />
              ))}
          </MeetingTypeList>
        </>
      )}
    </>
  )
}

export default MeetingTypeSelection
